import { useQuery, type ApolloError } from '@apollo/client'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GET_LAST_ORDER,
  isUnavailable,
  screenPath,
  toLastOrderModel,
  type ILastOrder,
  type ILastOrderVariables,
  type LastOrderModel,
  type ReorderLine,
  type UnavailableReason,
} from '@commerce/core'
import { usePrefillCart } from './usePrefillCart'
import { usePresentationContext } from './usePresentationContext'
import { useReorderAvailability } from './useReorderAvailability'

export interface UseRepeatLastOrder {
  /** The order worth offering to repeat, or `null` when there is none. */
  order: LastOrderModel | null
  /** Whether a catalog check has already run *for this order*. */
  checked: boolean
  /** The lines that can go back in the cart. Empty until a check has run. */
  available: ReorderLine[]
  /** The lines that cannot, with the reason. Empty until a check has run. */
  unavailable: (ReorderLine & { availability: UnavailableReason })[]
  /** A catalog check or a cart write is in flight. */
  busy: boolean
  error: ApolloError | undefined
  repeat: () => Promise<void>
}

/**
 * "Repetir mi último pedido", end to end.
 *
 * `lastOrder` is the server naming the shopper's most recent order, which is
 * what makes both halves of this honest: the date said out loud, and the lines
 * put back. Nothing here is inferred from the order history's first row.
 *
 * **No order, no offer.** A customer who has never bought anything gets
 * `order: null`, and the screen renders nothing at all — no button, and above
 * all no date. That is the same rule the suggested basket and the promotion
 * reels follow, and the reason `toLastOrderModel` also returns `null` for an
 * order whose lines resolve to nothing.
 *
 * **Two taps when something is missing, one when nothing is.** The first tap
 * buys the catalog check. If the whole order is still sellable it goes
 * straight to the cart — the common case, and the one the design is about. If
 * anything is not, it stops and names it, and a second, informed tap adds what
 * is left. A shopper who lands on a cart two items short with no explanation
 * has been lied to; an alert explaining a cart that is *already* short is the
 * same lie told afterwards.
 *
 * **The check is keyed by order id.** `lastOrder` can change under a tap — the
 * shopper places an order in another tab, Apollo refetches — and a resolved
 * check describes the order it was started for, not whichever one is on screen
 * when it lands. Both the stored result and the commit are guarded by that id,
 * so a stale reply is discarded rather than used to write a different order's
 * lines into the cart.
 *
 * Unlike the order-detail screen this *adds* rather than sets: `prefillCart`
 * with `replaceCart: false`. The home card offers to repeat an order on top of
 * whatever the shopper has already built, and throwing that away with no
 * warning is not a thing a convenience button gets to do.
 */
export const useRepeatLastOrder = (): UseRepeatLastOrder => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const { locale } = usePresentationContext()

  const { data } = useQuery<ILastOrder, ILastOrderVariables>(GET_LAST_ORDER, {
    variables: { storefrontName: slug, sessionUid: sessionId },
  })

  // No error is surfaced on purpose. This is an offer the shopper did not ask
  // for; a storefront that cannot answer it simply makes no offer, and an
  // alert about a query nobody triggered is noise on the home screen.
  const order = useMemo(() => toLastOrderModel(data?.lastOrder, locale), [data, locale])

  const { check, loading: checking, error: catalogError } = useReorderAvailability()
  const { addBasket, loading: adding, error: cartError } = usePrefillCart()

  // Tagged with the order it describes, so a result that outlived its order is
  // ignored rather than shown against a different one.
  const [result, setResult] = useState<{ orderId: string; lines: ReorderLine[] } | null>(null)

  // The order on screen *now*, readable from inside an awaited callback — the
  // render closure's copy is the one from the render that handled the tap.
  // Written during render rather than from an effect on purpose: an effect
  // leaves a window in which the ref still names the previous order, and that
  // window is exactly the one an in-flight catalog read lands in.
  const orderId = order?.id ?? null
  const orderIdRef = useRef(orderId)
  orderIdRef.current = orderId

  const lines = result && result.orderId === orderId ? result.lines : null
  const available = useMemo(
    () => (lines ?? []).filter((line) => !isUnavailable(line)),
    [lines],
  )
  const unavailable = useMemo(() => (lines ?? []).filter(isUnavailable), [lines])

  const addAndGo = useCallback(
    async (toAdd: readonly ReorderLine[]) => {
      const added = await addBasket(toAdd)
      // `onError` inside `usePrefillCart` turns a failure into a resolved
      // result with no data. Navigating on that would land the shopper on a
      // cart the mutation never wrote.
      if (added) navigate(screenPath('cart', { slug, sessionId }))
    },
    [addBasket, navigate, slug, sessionId],
  )

  const repeat = useCallback(async () => {
    if (!order) return

    // Second tap: availability is known and on screen, so add what survived.
    if (lines) {
      if (available.length > 0) await addAndGo(available)
      return
    }

    const resolved = await check(order.quantities)
    // No catalog, no idea what is still sellable — so nothing is added and
    // nothing is claimed. The button stays armed as the retry.
    if (!resolved) return
    // The offer moved on while the catalog was being read. These lines belong
    // to an order that is no longer the last one.
    if (orderIdRef.current !== order.id) return

    setResult({ orderId: order.id, lines: resolved })

    const sellable = resolved.filter((line) => !isUnavailable(line))
    // Everything still sells: one tap was the whole interaction. Anything less
    // stops here so the shopper reads what is missing before committing.
    if (sellable.length === resolved.length) await addAndGo(sellable)
  }, [order, lines, available, addAndGo, check])

  return {
    order,
    checked: lines !== null,
    available,
    unavailable,
    busy: checking || adding,
    error: catalogError ?? cartError,
    repeat,
  }
}
